/**
 * src/api/measurementApi.js
 *
 * Thin client for the measurement programme's read and metric-config
 * endpoints (msr 2.7, GH #7130; docs/measurement/architecture.md §6.1).
 *
 * The frontend never holds its own copy of a metric definition: every name,
 * formula, unit, direction and canonical criterion the Metric Explorer shows
 * comes from `getRegistry()` (AD-8). The parity test in
 * src/components/improvement/registryParity.test.js enforces that.
 *
 * Both endpoints resolve the tenant from the caller's JWT (resolveTenantId,
 * AUTH-B-002); nothing here sends a tenant id.
 */

import { azureGet, azurePost } from './azureClient';

const READ = '/api/measurement-read';

/** view=registry — { frameworkVersion, domains, tiers, dimensions, kpiGroups, metrics, baseMeasures, specDeviations } */
export async function getRegistry() {
  const res = await azureGet(READ, { view: 'registry' });
  return res.registry;
}

/** view=summary — { frameworkVersion, metrics: [{ id, latest, config, criteriaSource }] } */
export async function getSummary() {
  return azureGet(READ, { view: 'summary' });
}

/** view=metric — definition, config (with history), criteria, series, latest. */
export async function getMetric(metricId, periods) {
  return azureGet(READ, { view: 'metric', id: metricId, periods });
}

/** view=evidence — one run's evidence refs for one metric, resolved against current records. */
export async function getEvidence(runId, metricId) {
  return azureGet(READ, { view: 'evidence', run: runId, metric: metricId });
}

/**
 * Change one metric's tenant configuration. `reason` is required (3–500
 * characters); `rowVersion` is the config version last read, null only when
 * the metric has no config yet. A stale version is refused with 409.
 * Returns { ok, config, auditLogged }.
 */
export async function saveMetricConfig({ metricId, changes, reason, rowVersion }) {
  return azurePost('/api/measurement-config-write', {
    metricId,
    changes,
    reason,
    rowVersion: rowVersion ?? null,
  });
}

/**
 * Record a metric review (msr 2.7b). `outcome` is confirmed |
 * criteria-changed | retire-proposed; `note` is required for the last two.
 * The reviewer, time and capacity are stamped by the server. `rowVersion` is
 * the review record's version last read (null only when the metric has none
 * yet); a stale version is refused with 409.
 * Returns { ok, review, reviews: { version, rowVersion, count }, auditLogged }.
 */
export async function recordMetricReview({ metricId, outcome, note, rowVersion }) {
  return azurePost('/api/measurement-config-write', {
    action: 'record-review',
    metricId,
    outcome,
    note: note || null,
    rowVersion: rowVersion ?? null,
  });
}

/**
 * view=posture — the tenant's control posture as the daily control-status
 * pulse recorded it (GH #7262): { status: 'ok', day, applicable, compliant,
 * partial, nonCompliant, score } or { status: 'not-assessed', reason, day }.
 * The dashboard's one control figure; any dashboard role may read it.
 */
export async function getControlPosture() {
  const res = await azureGet(READ, { view: 'posture' });
  return res.posture;
}

/**
 * view=posture, whole response (msr 4.10a): `{ posture, publish: { enabled } }`.
 * `publish.enabled` is the server's MSR_PUBLISH_ENABLED switch (D26) — the
 * browser's only source for it, and cosmetic: the server enforces it.
 */
export async function getPostureRead() {
  return azureGet(READ, { view: 'posture' });
}

/**
 * view=exec (msr 4.8/4.9) — the latest executive result, its 24-period
 * series, the latest run's domain × dimension cells and the scope events.
 * Admin only and allow-listed (plan D1 A): a tenant not named in
 * MSR_EXEC_VIEW_TENANTS gets 404, thrown as an error with httpStatus 404.
 * Silent: every caller handles the failure itself (the page shows its own
 * error, the Explorer probe hides its link), and a 404 off the allow-list is
 * the expected answer, not a defect to banner or auto-report.
 */
export async function getExec() {
  return azureGet(READ, { view: 'exec' }, { silent: true });
}

/**
 * msr 4.10b — the measurement close a management review rested on (its
 * server-stamped pin) and the pack built from it. format 'json' →
 * { reviewId, pin, content, integrity, restatedSince }; 'pdf' →
 * { reviewId, integrity, filename, pdfBase64 }. 404 while the publish switch
 * is off; tenant admins and managers only.
 */
export async function getManagementReviewPack(reviewId, format = 'json') {
  return azureGet('/api/management-review-pack', { reviewId, format });
}
